import React, { useState, useEffect } from 'react';
import { docsApi } from '../../api';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const ACCESS_LABEL = { VIEW: 'Viewed', SEARCH_HIT: 'Found in search', UPDATE: 'Edited' };

export default function DocumentDetailModal({ doc, onClose, onEdit }) {
  const [history, setHistory]     = useState([]);
  const [loadingH, setLoadingH]   = useState(false);
  const [tab, setTab]             = useState('content'); // 'content' | 'history'

  useEffect(() => {
    if (tab === 'history' && doc?.id) {
      setLoadingH(true);
      docsApi.history(doc.id)
        .then(r => setHistory(r.data))
        .catch(() => {})
        .finally(() => setLoadingH(false));
    }
  }, [tab, doc?.id]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!doc) return null;

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="modal-title" style={{ marginBottom: '0.25rem' }}>{doc.title}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              👁 {doc.hitCount} views · last accessed {timeAgo(doc.lastAccessedAt)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button className="btn-ghost" style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }} onClick={() => onEdit(doc)}>✎ Edit</button>
            <button className="btn-icon" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--navy-border)', padding: '0 1.5rem' }}>
          {['content', 'history'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                background: 'none', border: 'none', fontFamily: 'inherit',
                padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.875rem',
                color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
                borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: '-1px', fontWeight: tab === t ? 600 : 400,
              }}
            >
              {t === 'content' ? '📄 Content' : '🕐 History'}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {tab === 'content' && (
            <>
              {doc.tags?.length > 0 && (
                <div className="tags-row" style={{ marginBottom: '1.25rem' }}>
                  {doc.tags.map(t => <span key={t} className="tag-badge">{t}</span>)}
                </div>
              )}
              <div style={{
                background: 'var(--input-bg)', borderRadius: 'var(--radius-sm)',
                padding: '1.25rem', fontSize: '0.9rem', lineHeight: 1.8,
                color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: '50vh', overflowY: 'auto',
              }}>
                {doc.content}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '1rem', display: 'flex', gap: '1.5rem' }}>
                <span>Created {new Date(doc.createdAt).toLocaleDateString()}</span>
                <span>Updated {new Date(doc.updatedAt).toLocaleDateString()}</span>
              </div>
            </>
          )}

          {tab === 'history' && (
            loadingH
              ? <div className="loading-overlay"><span className="spinner" /></div>
              : history.length === 0
                ? <div className="empty-state"><div className="empty-state-icon">🕐</div><div className="empty-state-title">No history yet</div></div>
                : <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    {history.map(h => (
                      <div key={h.id} className="history-item">
                        <div className={`history-dot ${h.accessType}`} />
                        <div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                            {ACCESS_LABEL[h.accessType] ?? h.accessType}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {new Date(h.accessedAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
          )}
        </div>
      </div>
    </div>
  );
}
